import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Archive,
  ArchiveRestore,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  CornerDownRight,
  GripVertical,
  History,
  MessageSquare,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react';
import type {
  TaskHistoryEntry,
  TaskStatusColor,
  TaskStatusDef,
  TaskStatusKind,
  TaskTreeNodeType,
} from '@bits/shared/zod';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

const STATUS_COLORS: readonly TaskStatusColor[] = [
  'slate',
  'blue',
  'emerald',
  'amber',
  'rose',
  'violet',
  'cyan',
  'orange',
];

const DOT: Record<TaskStatusColor, string> = {
  slate: 'bg-slate-400',
  blue: 'bg-blue-500',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  rose: 'bg-rose-500',
  violet: 'bg-violet-500',
  cyan: 'bg-cyan-500',
  orange: 'bg-orange-500',
};

type PendingRevert = { task: TaskTreeNodeType; from: TaskStatusDef; to: TaskStatusDef };

export function TaskBoard({ projectId }: { projectId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [dragging, setDragging] = React.useState<TaskTreeNodeType | null>(null);
  const [pendingRevert, setPendingRevert] = React.useState<PendingRevert | null>(null);
  const [openTaskId, setOpenTaskId] = React.useState<string | null>(null);
  const [manageOpen, setManageOpen] = React.useState(false);
  const [addingTo, setAddingTo] = React.useState<TaskStatusDef | null>(null);
  const [deleting, setDeleting] = React.useState<TaskTreeNodeType | null>(null);
  const [draggingColumn, setDraggingColumn] = React.useState<TaskStatusDef | null>(null);
  // Read during the same gesture that sets it, so a ref rather than state.
  const draggingColumnRef = React.useRef(false);

  const tasksQuery = useQuery({
    queryKey: ['tasks', projectId],
    queryFn: () => api.listProjectTasks(projectId),
  });
  const statusesQuery = useQuery({
    queryKey: ['task-statuses', projectId],
    queryFn: () => api.listTaskStatuses(projectId),
  });

  const refresh = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tasks', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['task-statuses', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] }),
      queryClient.invalidateQueries({ queryKey: ['task-history'] }),
    ]);
  }, [queryClient, projectId]);

  const statusMutation = useMutation({
    mutationFn: (v: { id: string; statusId: string }) =>
      api.updateTask(v.id, { statusId: v.statusId }),
    onSuccess: async () => {
      setErrorMessage(null);
      await refresh();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const revertMutation = useMutation({
    mutationFn: (v: { id: string; reason: string; toStatusId: string }) =>
      api.reopenTask(v.id, v.reason, v.toStatusId),
    onSuccess: async () => {
      setErrorMessage(null);
      setPendingRevert(null);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['task-history'] });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.archiveTask(id),
    onSuccess: async () => {
      setErrorMessage(null);
      setDeleting(null);
      if (openTaskId === deleting?.id) setOpenTaskId(null);
      await refresh();
    },
    onError: (err) => {
      setDeleting(null);
      setErrorMessage(toMessage(err));
    },
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => api.reorderTaskStatuses(projectId, orderedIds),
    onSuccess: async () => {
      setErrorMessage(null);
      await refresh();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const statuses = statusesQuery.data ?? [];
  const tasks = React.useMemo(() => flattenTree(tasksQuery.data ?? []), [tasksQuery.data]);

  // Archived columns disappear once nothing is left in them, but stay visible
  // (and read-only) while they still hold work.
  const visible = statuses.filter((s) => !s.archived || tasks.some((t) => t.statusId === s.id));

  const byStatus = new Map<string, TaskTreeNodeType[]>();
  for (const s of statuses) byStatus.set(s.id, []);
  for (const t of tasks) byStatus.get(t.statusId)?.push(t);

  function moveTask(task: TaskTreeNodeType, to: TaskStatusDef): void {
    if (task.statusId === to.id) return;
    const from = statuses.find((s) => s.id === task.statusId);
    if (!from) return;
    if (to.position < from.position) {
      setPendingRevert({ task, from, to });
      return;
    }
    statusMutation.mutate({ id: task.id, statusId: to.id });
  }

  function handleDragEnd(event: DragEndEvent): void {
    setDragging(null);
    setDraggingColumn(null);
    const over = event.over;
    if (!over) return;

    // Columns and cards share one context; the active id says which kind moved.
    const movedColumn = statuses.find((s) => s.id === event.active.id);
    const wasColumn = draggingColumnRef.current;
    draggingColumnRef.current = false;
    if (movedColumn && wasColumn) {
      // The board hides archived columns, but a reorder has to account for every
      // status — so resolve the drop against the full list, not the visible one.
      const from = statuses.findIndex((s) => s.id === movedColumn.id);
      const to = statuses.findIndex((s) => s.id === over.id);
      if (from === -1 || to === -1 || from === to) return;
      const next = [...statuses];
      const [lifted] = next.splice(from, 1);
      if (!lifted) return;
      next.splice(to, 0, lifted);
      reorderMutation.mutate(next.map((s) => s.id));
      return;
    }

    const task = tasks.find((t) => t.id === event.active.id);
    const target =
      statuses.find((s) => s.id === over.id) ??
      statuses.find((s) => s.id === tasks.find((t) => t.id === over.id)?.statusId);
    if (task && target) moveTask(task, target);
  }

  function handleDragStart(event: DragStartEvent): void {
    const column = statuses.find((s) => s.id === event.active.id);
    if (column) {
      draggingColumnRef.current = true;
      setDraggingColumn(column);
      return;
    }
    setDragging(tasks.find((t) => t.id === event.active.id) ?? null);
  }

  const openTask = tasks.find((t) => t.id === openTaskId) ?? null;

  return (
    <div className="flex flex-col gap-4">
      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>That move was rejected</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-center justify-end">
        <Button size="sm" variant="outline" onClick={() => setManageOpen(true)}>
          <Settings2 className="size-4" />
          Manage columns
        </Button>
      </div>

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setDragging(null);
          setDraggingColumn(null);
          draggingColumnRef.current = false;
        }}
      >
        <div className="flex gap-3 overflow-x-auto pb-2">
          <SortableContext
            items={visible.map((s) => s.id)}
            strategy={horizontalListSortingStrategy}
          >
            {visible.map((status) => (
              <BoardColumn
                key={status.id}
                status={status}
                tasks={byStatus.get(status.id) ?? []}
                onOpenTask={setOpenTaskId}
                onMove={moveTask}
                onAddTask={setAddingTo}
                onDeleteTask={setDeleting}
                statuses={statuses}
              />
            ))}
          </SortableContext>
          <button
            type="button"
            onClick={() => setManageOpen(true)}
            className="flex h-fit w-11 shrink-0 flex-col items-center gap-2 rounded-2xl border border-dashed border-[hsl(var(--border))] py-4 text-[hsl(var(--muted-foreground))] transition-colors hover:border-[hsl(var(--brand))] hover:text-[hsl(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            title="Add a status"
          >
            <Plus className="size-4" />
          </button>
        </div>

        <DragOverlay dropAnimation={null}>
          {dragging ? <TaskCard task={dragging} overlay /> : null}
          {draggingColumn ? (
            <div className="w-72 rounded-2xl border border-[hsl(var(--brand))] bg-[hsl(var(--card))] px-4 py-3 shadow-lg">
              <div className="flex items-center gap-2">
                <span className={cn('size-2 rounded-full', DOT[draggingColumn.color])} />
                <span className="text-sm font-semibold">{draggingColumn.label}</span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <RevertDialog
        pending={pendingRevert}
        isSaving={revertMutation.isPending}
        onCancel={() => setPendingRevert(null)}
        onConfirm={(reason) => {
          if (pendingRevert) {
            revertMutation.mutate({
              id: pendingRevert.task.id,
              reason,
              toStatusId: pendingRevert.to.id,
            });
          }
        }}
      />

      <TaskDetailDialog
        task={openTask}
        statuses={statuses}
        onClose={() => setOpenTaskId(null)}
        onMove={moveTask}
        onSaved={refresh}
      />

      <ManageStatusesDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        projectId={projectId}
        statuses={statuses}
      />

      <QuickAddTaskDialog
        projectId={projectId}
        status={addingTo}
        onClose={() => setAddingTo(null)}
        onCreated={refresh}
      />

      <DeleteTaskDialog
        task={deleting}
        isSaving={deleteMutation.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) deleteMutation.mutate(deleting.id);
        }}
      />
    </div>
  );
}

type QuickAddValues = { title: string; description: string; assigneeId: string; dueOn: string };

function QuickAddTaskDialog({
  projectId,
  status,
  onClose,
  onCreated,
}: {
  projectId: string;
  status: TaskStatusDef | null;
  onClose: () => void;
  onCreated: () => Promise<void>;
}): JSX.Element {
  const [error, setError] = React.useState<string | null>(null);
  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
    enabled: status !== null,
  });
  const form = useForm<QuickAddValues>({
    defaultValues: { title: '', description: '', assigneeId: '', dueOn: '' },
  });

  React.useEffect(() => {
    if (status) {
      form.reset({ title: '', description: '', assigneeId: '', dueOn: '' });
      setError(null);
    }
  }, [status, form]);

  const createMutation = useMutation({
    mutationFn: (v: QuickAddValues) => {
      if (!status) throw new Error('No column chosen');
      return api.createTask(projectId, {
        title: v.title.trim(),
        description: v.description.trim() || undefined,
        assigneeId: v.assigneeId,
        dueOn: v.dueOn || undefined,
        statusId: status.id,
      });
    },
    onSuccess: async () => {
      setError(null);
      onClose();
      await onCreated();
    },
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <Dialog open={status !== null} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New task in {status?.label ?? 'this column'}</DialogTitle>
          <DialogDescription>
            The task starts in this column straight away. Subtasks are added from the New task
            button above the board.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
          className="flex flex-col gap-3"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quick-task-title">Title</Label>
            <Input
              id="quick-task-title"
              autoFocus
              maxLength={240}
              {...form.register('title', {
                required: 'Enter a title',
                validate: (v) => v.trim().length > 0 || 'Enter a title',
              })}
            />
            {form.formState.errors.title ? (
              <p className="text-xs text-[hsl(var(--destructive))]">
                {form.formState.errors.title.message}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quick-task-description">Description</Label>
            <Textarea
              id="quick-task-description"
              rows={3}
              maxLength={4000}
              placeholder="What needs doing, and anything the assignee should know."
              {...form.register('description')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="quick-task-assignee">Assignee</Label>
              <Select
                id="quick-task-assignee"
                {...form.register('assigneeId', { required: 'Pick an assignee' })}
              >
                <option value="">Select…</option>
                {(peopleQuery.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              {form.formState.errors.assigneeId ? (
                <p className="text-xs text-[hsl(var(--destructive))]">
                  {form.formState.errors.assigneeId.message}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="quick-task-due">Due date</Label>
              <Input id="quick-task-due" type="date" {...form.register('dueOn')} />
            </div>
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Create failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Saving…' : 'Create task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteTaskDialog({
  task,
  isSaving,
  onCancel,
  onConfirm,
}: {
  task: TaskTreeNodeType | null;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const subtaskCount = task?.children.length ?? 0;
  return (
    <Dialog open={task !== null} onOpenChange={(open) => (!open ? onCancel() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete this task?</DialogTitle>
          <DialogDescription>
            {task
              ? `“${task.title}” leaves the board and everyone's My Tasks${
                  subtaskCount > 0
                    ? `, along with its ${subtaskCount} ${subtaskCount === 1 ? 'subtask' : 'subtasks'}`
                    : ''
                }. Its history and any time logged against it are kept.`
              : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" disabled={isSaving} onClick={onConfirm}>
            {isSaving ? 'Deleting…' : 'Delete task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BoardColumn({
  status,
  tasks,
  statuses,
  onOpenTask,
  onMove,
  onAddTask,
  onDeleteTask,
}: {
  status: TaskStatusDef;
  tasks: TaskTreeNodeType[];
  statuses: TaskStatusDef[];
  onOpenTask: (id: string) => void;
  onMove: (task: TaskTreeNodeType, to: TaskStatusDef) => void;
  onAddTask: (status: TaskStatusDef) => void;
  onDeleteTask: (task: TaskTreeNodeType) => void;
}): JSX.Element {
  const canAdd = !status.archived && status.kind === 'open';
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: status.id,
    disabled: status.archived,
  });
  const {
    attributes,
    listeners,
    setNodeRef: setSortRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  return (
    <div
      ref={(node) => {
        setSortRef(node);
        setDropRef(node);
      }}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex w-72 shrink-0 flex-col rounded-2xl border bg-[hsl(var(--card))] transition-colors',
        isOver
          ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/[0.04]'
          : 'border-[hsl(var(--border))]',
        status.archived && 'opacity-60',
        isDragging && 'opacity-40',
      )}
    >
      <div className="group/header flex items-center gap-2 px-4 pb-3 pt-4">
        <button
          type="button"
          {...listeners}
          {...attributes}
          aria-label={`Reorder ${status.label}`}
          className="-ml-1 cursor-grab touch-none rounded p-0.5 text-[hsl(var(--muted-foreground))] opacity-0 transition-opacity hover:text-[hsl(var(--foreground))] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] group-hover/header:opacity-100"
        >
          <GripVertical className="size-3.5" />
        </button>
        <span className={cn('size-2 shrink-0 rounded-full', DOT[status.color])} />
        <h3 className="truncate text-sm font-semibold text-[hsl(var(--foreground))]">
          {status.label}
        </h3>
        {status.kind === 'finished' ? (
          <span className="rounded bg-[hsl(var(--muted))] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            done
          </span>
        ) : null}
        {status.archived ? (
          <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            archived
          </span>
        ) : null}
        <span className="ml-auto rounded-full bg-[hsl(var(--muted))] px-2 py-0.5 text-xs tabular-nums text-[hsl(var(--muted-foreground))]">
          {tasks.length}
        </span>
        {canAdd ? (
          <StepButton onClick={() => onAddTask(status)} label={`Add a task to ${status.label}`}>
            <Plus className="size-3.5" />
          </StepButton>
        ) : null}
      </div>

      <div className="flex min-h-24 flex-col gap-2 px-3 pb-3">
        {tasks.map((t) => (
          <DraggableCard
            key={t.id}
            task={t}
            statuses={statuses}
            onOpen={onOpenTask}
            onMove={onMove}
            onDelete={onDeleteTask}
          />
        ))}
        {tasks.length === 0 ? (
          <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-[hsl(var(--border))] py-6 text-xs text-[hsl(var(--muted-foreground))]">
            {status.archived ? 'Archived' : 'Drop a task here'}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DraggableCard({
  task,
  statuses,
  onOpen,
  onMove,
  onDelete,
}: {
  task: TaskTreeNodeType;
  statuses: TaskStatusDef[];
  onOpen: (id: string) => void;
  onMove: (task: TaskTreeNodeType, to: TaskStatusDef) => void;
  onDelete: (task: TaskTreeNodeType) => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });
  const here = statuses.findIndex((s) => s.id === task.statusId);
  const prev = here > 0 ? statuses[here - 1] : undefined;
  const next = here >= 0 && here < statuses.length - 1 ? statuses[here + 1] : undefined;

  return (
    <div ref={setNodeRef} className={cn(isDragging && 'opacity-40')}>
      <TaskCard
        task={task}
        onOpen={() => onOpen(task.id)}
        onStepBack={prev ? () => onMove(task, prev) : undefined}
        onStepForward={next ? () => onMove(task, next) : undefined}
        onDelete={() => onDelete(task)}
        dragHandleProps={{ ...listeners, ...attributes }}
      />
    </div>
  );
}

function TaskCard({
  task,
  onOpen,
  onStepBack,
  onStepForward,
  onDelete,
  dragHandleProps,
  overlay = false,
}: {
  task: TaskTreeNodeType;
  onOpen?: () => void;
  onStepBack?: () => void;
  onStepForward?: () => void;
  onDelete?: () => void;
  dragHandleProps?: Record<string, unknown>;
  overlay?: boolean;
}): JSX.Element {
  const overdue = task.dueOn !== null && task.statusKind !== 'finished' && task.dueOn < todayIso();

  return (
    <div
      {...dragHandleProps}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onOpen) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'group flex cursor-grab touch-none flex-col gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3 text-left transition-all',
        'hover:border-[hsl(var(--brand))]/40 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]',
        overlay && 'rotate-2 cursor-grabbing shadow-lg',
      )}
    >
      <div className="flex items-start gap-1.5">
        {task.parentTaskId ? (
          <CornerDownRight className="mt-0.5 size-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
        ) : null}
        <span className="text-sm font-medium leading-snug text-[hsl(var(--foreground))]">
          {task.title}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
        <span className="truncate">{task.assigneeName ?? task.assigneeId}</span>
        {task.dueOn ? (
          <span
            className={cn(
              'inline-flex items-center gap-1',
              overdue && 'font-medium text-[hsl(var(--destructive))]',
            )}
          >
            <CalendarClock className="size-3" />
            {task.dueOn}
          </span>
        ) : null}
        {task.commentCount > 0 ? (
          <span
            className="inline-flex items-center gap-1"
            aria-label={`${task.commentCount} comment${task.commentCount === 1 ? '' : 's'}`}
          >
            <MessageSquare className="size-3" />
            {task.commentCount}
          </span>
        ) : null}
      </div>

      {onStepBack || onStepForward || onDelete ? (
        <div className="flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {onStepBack ? (
            <StepButton onClick={onStepBack} label="Move back">
              <ChevronLeft className="size-3.5" />
            </StepButton>
          ) : null}
          {onStepForward ? (
            <StepButton onClick={onStepForward} label="Move forward">
              <ChevronRight className="size-3.5" />
            </StepButton>
          ) : null}
          {onDelete ? (
            <span className="ml-auto">
              <StepButton onClick={onDelete} label="Delete task" tone="danger">
                <Trash2 className="size-3.5" />
              </StepButton>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function StepButton({
  onClick,
  label,
  tone = 'default',
  children,
}: {
  onClick: () => void;
  label: string;
  tone?: 'default' | 'danger';
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'rounded-md border border-[hsl(var(--border))] p-1 text-[hsl(var(--muted-foreground))] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]',
        tone === 'danger'
          ? 'hover:border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))]'
          : 'hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]',
      )}
    >
      {children}
    </button>
  );
}

function RevertDialog({
  pending,
  isSaving,
  onCancel,
  onConfirm,
}: {
  pending: PendingRevert | null;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}): JSX.Element {
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (pending) setReason('');
  }, [pending]);

  const isReopen = pending?.from.kind === 'finished';

  return (
    <Dialog open={pending !== null} onOpenChange={(open) => (!open ? onCancel() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isReopen ? 'Reopen this task' : 'Correct the status'}</DialogTitle>
          <DialogDescription>
            {pending
              ? `Moving “${pending.task.title}” back from ${pending.from.label} to ${pending.to.label}. This is recorded against the task with your name and reason.`
              : null}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim()) onConfirm(reason.trim());
          }}
          className="flex flex-col gap-5"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="revert-reason">Reason</Label>
            <Input
              id="revert-reason"
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              placeholder="e.g. moved by mistake"
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || reason.trim().length === 0}>
              {isSaving ? 'Saving…' : pending ? `Move to ${pending.to.label}` : 'Move'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type TaskEditValues = {
  title: string;
  description: string;
  assigneeId: string;
  dueOn: string;
  actualHours: string;
};

function TaskDetailDialog({
  task,
  statuses,
  onClose,
  onMove,
  onSaved,
}: {
  task: TaskTreeNodeType | null;
  statuses: TaskStatusDef[];
  onClose: () => void;
  onMove: (task: TaskTreeNodeType, to: TaskStatusDef) => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);
  // History must reflect the move or edit that was just made, so the cached
  // list is shown instantly but always refetched when the sheet opens.
  const historyQuery = useQuery({
    queryKey: ['task-history', task?.id],
    queryFn: () => api.getTaskHistory(task!.id),
    enabled: task !== null,
    staleTime: 0,
  });
  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
    enabled: editing,
  });
  const commentsQuery = useQuery({
    queryKey: ['task-comments', task?.id],
    queryFn: () => api.listTaskComments(task!.id),
    enabled: task !== null,
    staleTime: 0,
  });
  const [commentBody, setCommentBody] = React.useState('');
  const [commentError, setCommentError] = React.useState<string | null>(null);
  const commentMutation = useMutation({
    mutationFn: () => {
      if (!task) throw new Error('No task open');
      return api.addTaskComment(task.id, { body: commentBody.trim() });
    },
    onSuccess: async () => {
      setCommentBody('');
      setCommentError(null);
      await Promise.all([commentsQuery.refetch(), historyQuery.refetch(), onSaved()]);
    },
    onError: (err) => setCommentError(toMessage(err)),
  });

  const editForm = useForm<TaskEditValues>({
    defaultValues: { title: '', description: '', assigneeId: '', dueOn: '', actualHours: '' },
  });
  const startEditing = () => {
    if (!task) return;
    editForm.reset({
      title: task.title,
      description: task.description ?? '',
      assigneeId: task.assigneeId,
      dueOn: task.dueOn ?? '',
      actualHours: task.actualHours === null ? '' : String(task.actualHours),
    });
    setEditError(null);
    setEditing(true);
  };
  const editMutation = useMutation({
    mutationFn: (v: TaskEditValues) => {
      if (!task) throw new Error('No task open');
      const body: Parameters<typeof api.updateTask>[1] = {};
      if (v.title !== task.title) body.title = v.title;
      const description = v.description.trim() || null;
      if (description !== task.description) body.description = description;
      if (v.assigneeId !== task.assigneeId) body.assigneeId = v.assigneeId;
      if ((v.dueOn || null) !== task.dueOn) body.dueOn = v.dueOn || null;
      const hours = v.actualHours === '' ? null : Number(v.actualHours);
      if (hours !== task.actualHours) body.actualHours = hours;
      return api.updateTask(task.id, body);
    },
    onSuccess: async () => {
      setEditing(false);
      setEditError(null);
      await Promise.all([historyQuery.refetch(), onSaved()]);
    },
    onError: (err) => setEditError(toMessage(err)),
  });

  const current = statuses.find((s) => s.id === task?.statusId);

  return (
    <Sheet
      open={task !== null}
      onOpenChange={(open) => {
        if (!open) {
          setEditing(false);
          setCommentBody('');
          setCommentError(null);
          onClose();
        }
      }}
    >
      <SheetContent>
        {task ? (
          <>
            <SheetHeader>
              <SheetTitle>{task.title}</SheetTitle>
              <SheetDescription>
                {task.parentTaskId ? 'Subtask' : 'Top-level task'} in this project.
              </SheetDescription>
            </SheetHeader>

            <div className="flex flex-col gap-5">
              {editing ? (
                <form
                  onSubmit={editForm.handleSubmit((v) => editMutation.mutate(v))}
                  className="flex flex-col gap-3 rounded-xl border border-[hsl(var(--border))] p-4"
                >
                  <div className="flex flex-col gap-1.5">
                    <Label>Title</Label>
                    <Input {...editForm.register('title', { required: 'Enter a title' })} />
                    {editForm.formState.errors.title ? (
                      <p className="text-xs text-[hsl(var(--destructive))]">
                        {editForm.formState.errors.title.message}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Description</Label>
                    <Textarea
                      rows={3}
                      maxLength={4000}
                      placeholder="What needs doing, and anything the assignee should know."
                      {...editForm.register('description')}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <Label>Assignee</Label>
                      <Select
                        {...editForm.register('assigneeId', { required: 'Pick an assignee' })}
                      >
                        <option value="">Select…</option>
                        {(peopleQuery.data ?? []).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Select>
                      {editForm.formState.errors.assigneeId ? (
                        <p className="text-xs text-[hsl(var(--destructive))]">
                          {editForm.formState.errors.assigneeId.message}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label>Due date</Label>
                      <Input type="date" {...editForm.register('dueOn')} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label>Actual hours</Label>
                      <Input type="number" min="0" step="1" {...editForm.register('actualHours')} />
                    </div>
                  </div>
                  {editError ? (
                    <Alert variant="destructive">
                      <AlertTitle>Save failed</AlertTitle>
                      <AlertDescription>{editError}</AlertDescription>
                    </Alert>
                  ) : null}
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={editMutation.isPending}>
                      {editMutation.isPending ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </form>
              ) : (
                <dl className="relative grid grid-cols-2 gap-x-4 gap-y-4 rounded-xl bg-[hsl(var(--muted))]/50 p-4 text-sm">
                  <Field label="Status">
                    <span className="inline-flex items-center gap-1.5">
                      {current ? (
                        <span className={cn('size-2 rounded-full', DOT[current.color])} />
                      ) : null}
                      {task.statusLabel}
                    </span>
                  </Field>
                  <Field label="Assignee">{task.assigneeName ?? task.assigneeId}</Field>
                  <Field label="Due">{task.dueOn ?? '—'}</Field>
                  <Field label="Actual hours">
                    {task.actualHours === null ? '—' : `${task.actualHours}h`}
                  </Field>
                  <div className="col-span-2">
                    <Field label="Description">
                      {task.description ? (
                        <span className="whitespace-pre-wrap">{task.description}</span>
                      ) : (
                        <span className="text-[hsl(var(--muted-foreground))]">
                          No description yet.
                        </span>
                      )}
                    </Field>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <Button type="button" variant="outline" size="sm" onClick={startEditing}>
                      Edit details
                    </Button>
                  </div>
                </dl>
              )}

              <div className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                  Progress
                </span>
                <StatusTrack
                  statuses={statuses}
                  currentId={task.statusId}
                  onPick={(s) => {
                    onMove(task, s);
                    onClose();
                  }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--foreground))]">
                  <MessageSquare className="size-4 text-[hsl(var(--muted-foreground))]" />
                  Comments
                  {(commentsQuery.data ?? []).length > 0 ? (
                    <span className="text-xs font-normal text-[hsl(var(--muted-foreground))]">
                      ({(commentsQuery.data ?? []).length})
                    </span>
                  ) : null}
                </div>
                {commentsQuery.isPending ? (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Loading…</p>
                ) : (commentsQuery.data ?? []).length === 0 ? (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">No comments yet.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {(commentsQuery.data ?? []).map((c) => (
                      <li
                        key={c.id}
                        className="rounded-lg border border-[hsl(var(--border))] p-3 text-sm"
                      >
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                          <span className="font-medium text-[hsl(var(--foreground))]">
                            {c.authorName ?? c.authorId}
                          </span>
                          <span>·</span>
                          <time dateTime={c.createdAt}>{formatStamp(c.createdAt)}</time>
                        </div>
                        <div className="mt-1 whitespace-pre-wrap">{c.body}</div>
                      </li>
                    ))}
                  </ul>
                )}
                <form
                  className="flex flex-col gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (commentBody.trim() === '' || commentMutation.isPending) return;
                    commentMutation.mutate();
                  }}
                >
                  <Textarea
                    rows={2}
                    maxLength={5000}
                    placeholder="Add a comment…"
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        e.currentTarget.form?.requestSubmit();
                      }
                    }}
                  />
                  {commentError ? (
                    <Alert variant="destructive">
                      <AlertTitle>Comment not posted</AlertTitle>
                      <AlertDescription>{commentError}</AlertDescription>
                    </Alert>
                  ) : null}
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      size="sm"
                      disabled={commentBody.trim() === '' || commentMutation.isPending}
                    >
                      {commentMutation.isPending ? 'Posting…' : 'Post comment'}
                    </Button>
                  </div>
                </form>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--foreground))]">
                  <History className="size-4 text-[hsl(var(--muted-foreground))]" />
                  History
                </div>
                {historyQuery.isPending ? (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Loading…</p>
                ) : (historyQuery.data ?? []).length === 0 ? (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    No recorded activity yet.
                  </p>
                ) : (
                  <ol className="flex flex-col gap-2.5">
                    {(historyQuery.data ?? []).map((h) => (
                      <HistoryRow key={h.id} entry={h} statuses={statuses} />
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function StatusTrack({
  statuses,
  currentId,
  onPick,
}: {
  statuses: TaskStatusDef[];
  currentId: string;
  onPick: (status: TaskStatusDef) => void;
}): JSX.Element {
  const live = statuses.filter((s) => !s.archived || s.id === currentId);
  const currentIndex = live.findIndex((s) => s.id === currentId);

  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
      {live.map((s, i) => {
        const isCurrent = s.id === currentId;
        const isPast = i < currentIndex;

        return (
          <React.Fragment key={s.id}>
            {i > 0 ? (
              <span
                aria-hidden
                className={cn(
                  'h-px w-2 shrink-0',
                  isPast || isCurrent ? 'bg-[hsl(var(--border))]' : 'bg-[hsl(var(--border))]/50',
                )}
              />
            ) : null}

            {isCurrent ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand))]/[0.09] px-2.5 py-1 text-xs font-medium text-[hsl(var(--foreground))] ring-1 ring-[hsl(var(--brand))]/25">
                <span className={cn('size-1.5 rounded-full', DOT[s.color])} />
                {s.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onPick(s)}
                title={isPast ? `Move back to ${s.label}` : `Move to ${s.label}`}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
              >
                <span
                  className={cn(
                    'size-1.5 rounded-full',
                    isPast ? DOT[s.color] : 'bg-[hsl(var(--border))]',
                  )}
                />
                <span
                  className={cn(
                    isPast
                      ? 'text-[hsl(var(--muted-foreground))]'
                      : 'text-[hsl(var(--muted-foreground))]/70',
                  )}
                >
                  {s.label}
                </span>
              </button>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ManageStatusesDialog({
  open,
  onOpenChange,
  projectId,
  statuses,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  statuses: TaskStatusDef[];
}): JSX.Element {
  const queryClient = useQueryClient();
  const [label, setLabel] = React.useState('');
  const [kind, setKind] = React.useState<TaskStatusKind>('open');
  const [color, setColor] = React.useState<TaskStatusColor>('blue');
  const [error, setError] = React.useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['task-statuses', projectId] });
    await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
  };

  const createMutation = useMutation({
    mutationFn: () => api.createTaskStatus(projectId, { label: label.trim(), kind, color }),
    onSuccess: async () => {
      setLabel('');
      setError(null);
      await refresh();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (v: { id: string; archived?: boolean; kind?: TaskStatusKind }) =>
      api.updateTaskStatus(v.id, { archived: v.archived, kind: v.kind }),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => api.reorderTaskStatuses(projectId, orderedIds),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function onDragEnd(event: DragEndEvent): void {
    const over = event.over;
    if (!over || over.id === event.active.id) return;
    const from = statuses.findIndex((s) => s.id === event.active.id);
    const to = statuses.findIndex((s) => s.id === over.id);
    if (from === -1 || to === -1) return;
    const next = [...statuses];
    const [lifted] = next.splice(from, 1);
    if (!lifted) return;
    next.splice(to, 0, lifted);
    reorderMutation.mutate(next.map((s) => s.id));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Board columns</DialogTitle>
          <DialogDescription>
            Add as many statuses as this project needs, and drag to reorder them. Each one counts as
            open work or finished work — that classification is what open-task counts and project
            close read.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Could not save</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="flex flex-col gap-5">
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <SortableContext
              items={statuses.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="flex flex-col gap-1.5">
                {statuses.map((st) => (
                  <SortableStatusRow
                    key={st.id}
                    status={st}
                    onToggleArchive={() =>
                      updateMutation.mutate({ id: st.id, archived: !st.archived })
                    }
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (label.trim()) createMutation.mutate();
            }}
            className="flex flex-col gap-3 rounded-xl border border-dashed border-[hsl(var(--border))] p-4"
          >
            <span className="text-sm font-medium">Add a status</span>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="new-status-label">Name</Label>
                <Input
                  id="new-status-label"
                  value={label}
                  onChange={(e) => setLabel(e.currentTarget.value)}
                  placeholder="e.g. In Review"
                  maxLength={64}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="new-status-kind">Counts as</Label>
                <Select
                  id="new-status-kind"
                  value={kind}
                  onChange={(e) => setKind(e.currentTarget.value as TaskStatusKind)}
                >
                  <option value="open">Open work</option>
                  <option value="finished">Finished work</option>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="new-status-color">Color</Label>
                <Select
                  id="new-status-color"
                  value={color}
                  onChange={(e) => setColor(e.currentTarget.value as TaskStatusColor)}
                >
                  {STATUS_COLORS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={createMutation.isPending || !label.trim()}>
                <Plus className="size-4" />
                {createMutation.isPending ? 'Adding…' : 'Add status'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SortableStatusRow({
  status,
  onToggleArchive,
}: {
  status: TaskStatusDef;
  onToggleArchive: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: status.id,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2.5',
        status.archived && 'opacity-60',
        isDragging && 'z-10 shadow-lg',
      )}
    >
      <button
        type="button"
        {...listeners}
        {...attributes}
        aria-label={`Reorder ${status.label}`}
        className="cursor-grab touch-none rounded p-0.5 text-[hsl(var(--muted-foreground))] transition-colors hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
      >
        <GripVertical className="size-4" />
      </button>
      <span className={cn('size-2.5 shrink-0 rounded-full', DOT[status.color])} />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{status.label}</span>
      <Badge variant={status.kind === 'finished' ? 'default' : 'muted'}>{status.kind}</Badge>
      <span className="w-16 text-right text-xs tabular-nums text-[hsl(var(--muted-foreground))]">
        {status.taskCount} {status.taskCount === 1 ? 'task' : 'tasks'}
      </span>
      <StepButton onClick={onToggleArchive} label={status.archived ? 'Restore' : 'Archive'}>
        {status.archived ? (
          <ArchiveRestore className="size-3.5" />
        ) : (
          <Archive className="size-3.5" />
        )}
      </StepButton>
    </li>
  );
}

function HistoryRow({
  entry,
  statuses,
}: {
  entry: TaskHistoryEntry;
  statuses: TaskStatusDef[];
}): JSX.Element {
  const diff = entry.diff ?? {};
  const from = asString(diff.statusFrom) ?? asString(diff.from);
  const to = asString(diff.statusTo) ?? asString(diff.to);
  const label = (key: string): string => statuses.find((s) => s.key === key)?.label ?? key;

  return (
    <li className="flex flex-col gap-0.5 border-l-2 border-[hsl(var(--border))] pl-3 text-xs">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium text-[hsl(var(--foreground))]">
          {ACTION_LABELS[entry.action] ?? entry.action}
        </span>
        {from && to ? (
          <span className="text-[hsl(var(--muted-foreground))]">
            {label(from)} → {label(to)}
          </span>
        ) : null}
      </div>
      <div className="text-[hsl(var(--muted-foreground))]">
        {entry.actorName ?? entry.actorId ?? 'system'} · {formatStamp(entry.createdAt)}
      </div>
      {entry.reason ? (
        <div className="italic text-[hsl(var(--muted-foreground))]">“{entry.reason}”</div>
      ) : null}
    </li>
  );
}

const ACTION_LABELS: Record<string, string> = {
  'task.created': 'Created',
  'task.updated': 'Updated',
  'task.reopened': 'Reopened',
  'task.status_corrected': 'Status corrected',
  'task.archived': 'Deleted',
  'task.completed': 'Completed',
  'task.commented': 'Commented',
};

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-[hsl(var(--muted-foreground))]">{label}</dt>
      <dd className="text-[hsl(var(--foreground))]">{children}</dd>
    </div>
  );
}

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function formatStamp(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

export function flattenTree(nodes: TaskTreeNodeType[]): TaskTreeNodeType[] {
  const out: TaskTreeNodeType[] = [];
  const walk = (list: TaskTreeNodeType[]): void => {
    for (const n of list) {
      out.push(n);
      walk(n.children);
    }
  };
  walk(nodes);
  return out;
}
